import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { API } from '@/App';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  MessageSquare,
  CreditCard,
  Plus,
  Search,
  LogIn,
  X,
  TrendingUp,
  Globe,
  SlidersHorizontal,
  QrCode,
  Trash2,
  Layers,
  ShieldAlert,
  ShieldCheck
} from 'lucide-react';

// Feb 2026 — Platform-Manager-only subscription enums. Must stay in sync with
// backend `SUBSCRIPTION_*` allow-lists in routes/platform.py. Order here drives
// dropdown order in the Subscription editor modal.
const SUBSCRIPTION_STATUS_OPTIONS = [
  'pending_payment', 'payment_processing', 'active',
  'past_due', 'expired', 'cancelled', 'archived',
];
const SUBSCRIPTION_SOURCE_OPTIONS = [
  'stripe', 'bank_transfer', 'manual_admin', 'reseller', 'invoice',
];
const SUBSCRIPTION_BILLING_CYCLE_OPTIONS = [
  'monthly', 'annual', 'manual', 'one_time', 'custom',
];

// Feature-flag definitions surfaced in the Platform Admin "Features" modal.
// Keys must match backend DEFAULT_FEATURE_FLAGS; labels are user-facing.
const FEATURE_FLAG_DEFS = [
  { key: 'gap_library',                label: 'Gap Library' },
  { key: 'gap_best_practices',         label: 'Gap Best Practices Link' },
  { key: 'cases',                      label: 'Cases' },
  { key: 'validation_codes',           label: 'Validation Codes' },
  { key: 'profanity_filter',           label: 'Profanity Filter' },
  { key: 'feedback_alerts',            label: 'Feedback Alerts' },
  // Reduced Phase 1 (Feb 2026) — MS client dashboard rollout gate.
  // Both flags must be ON for the tenant's Reports page to show the new
  // KPI dashboard row. Leave off by default; enable only for pilot tenants.
  { key: 'products_mystery_shopping',  label: 'Product: Mystery Shopping' },
  { key: 'ms_new_dashboard',           label: 'MS · New Client Dashboard (beta)' },
];
const DEFAULT_FEATURE_FLAGS = FEATURE_FLAG_DEFS.reduce((acc, f) => ({ ...acc, [f.key]: false }), {});

// Jun 2026 — platform-wide Product Features panel. Two product-level switches
// (Management Insights, Ask satisFIND) across the four configurable products.
// Persisted server-side in `product_feature_configs`; applies to existing and
// new clients without touching any client record.
function ProductFeaturesPanel() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null); // `${product}:${feature}`
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/platform/product-features`, { credentials: 'include' });
        if (res.ok) setData(await res.json());
      } catch (_) { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const toggle = async (product, feature, next) => {
    const key = `${product}:${feature}`;
    setSaving(key);
    // Optimistic update.
    setData(prev => ({
      ...prev,
      configs: { ...prev.configs, [product]: { ...prev.configs[product], [feature]: next } },
    }));
    try {
      const res = await fetch(`${API}/platform/product-features/${product}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [feature]: next }),
      });
      if (!res.ok) throw new Error('save failed');
      const body = await res.json();
      setData(prev => ({
        ...prev,
        configs: { ...prev.configs, [product]: body.config },
      }));
      toast.success('Product feature updated');
    } catch (e) {
      // Revert.
      setData(prev => ({
        ...prev,
        configs: { ...prev.configs, [product]: { ...prev.configs[product], [feature]: !next } },
      }));
      toast.error('Could not update product feature');
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="product-features-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-[#1A3E6F]" />
          <CardTitle className="text-base text-[#0F172A]">Product Features</CardTitle>
        </div>
        <p className="text-xs text-[#64748B] mt-1">
          Platform-wide switches applied to every client of each product. Certificates
          are not configurable here and remain restricted to Mystery Shopping.
        </p>
      </CardHeader>
      <CardContent>
        {loading || !data ? (
          <p className="text-sm text-[#64748B]" data-testid="product-features-loading">Loading…</p>
        ) : (
          <div className="space-y-4">
            {data.products.map(product => (
              <div key={product} className="rounded-lg border border-[#E2E8F0] p-4"
                   data-testid={`product-features-row-${product}`}>
                <p className="text-sm font-semibold text-[#0F172A] mb-3">
                  {data.product_labels[product] || product}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {data.features.map(feature => {
                    const implemented = !!(data.implemented?.[product]?.[feature]);
                    const value = !!data.configs?.[product]?.[feature];
                    const key = `${product}:${feature}`;
                    return (
                      <div key={feature}
                           className="flex items-center justify-between gap-3 rounded-md bg-[#F8FAFC] px-3 py-2"
                           data-testid={`product-feature-${product}-${feature}`}>
                        <div className="min-w-0">
                          <p className="text-sm text-[#0F172A]">{data.feature_labels[feature] || feature}</p>
                          {!implemented && (
                            <Badge variant="outline"
                                   className="mt-1 text-[10px] border-amber-300 text-amber-700 bg-amber-50"
                                   data-testid={`product-feature-unsupported-${product}-${feature}`}>
                              {value ? 'Configured — not yet available' : 'Not yet available'}
                            </Badge>
                          )}
                        </div>
                        <Switch
                          checked={value}
                          disabled={saving === key}
                          onCheckedChange={(next) => toggle(product, feature, next)}
                          data-testid={`product-feature-switch-${product}-${feature}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <p className="text-[11px] text-[#94A3B8]">
              “Not yet available” means the product does not yet have a working implementation
              of this feature. The permission is saved and will take effect automatically once an
              implementation exists — no client data is exposed in the meantime.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PlatformDashboard() {
  const { checkAuth, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTenant, setNewTenant] = useState({ name: '', admin_email: '', admin_name: '', industry: 'restaurant', plan_type: 'vox_core' });
  const [creating, setCreating] = useState(false);
  const [createdCreds, setCreatedCreds] = useState(null);
  const [planOptions, setPlanOptions] = useState([]);
  const [updatingPlanFor, setUpdatingPlanFor] = useState(null);
  const [featuresTenant, setFeaturesTenant] = useState(null); // opens the Features modal
  const [featuresSaving, setFeaturesSaving] = useState(false);
  const [regeneratingQrs, setRegeneratingQrs] = useState(false);
  const [clearingResponsesFor, setClearingResponsesFor] = useState(null);
  // Feb 2026 — subscription editor state. Opens a modal for one tenant at a time.
  const [subscriptionTenant, setSubscriptionTenant] = useState(null);
  const [subscriptionDraft, setSubscriptionDraft] = useState({});
  const [locAllowance, setLocAllowance] = useState(null);
  const [locAllowanceInput, setLocAllowanceInput] = useState('');
  const [locAllowanceSaving, setLocAllowanceSaving] = useState(false);
  const [subscriptionSaving, setSubscriptionSaving] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState(null); // tenant pending suspend-confirm
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendBusy, setSuspendBusy] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, tenantsRes, plansRes] = await Promise.all([
        fetch(`${API}/platform/stats`, { credentials: 'include' }),
        fetch(`${API}/platform/tenants`, { credentials: 'include' }),
        fetch(`${API}/platform/plans`, { credentials: 'include' }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (tenantsRes.ok) {
        const data = await tenantsRes.json();
        setTenants(data.tenants || []);
      }
      if (plansRes.ok) {
        const p = await plansRes.json();
        setPlanOptions(p.plans || []);
      }
    } catch (e) {
      console.error('Failed to fetch platform data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleImpersonate = async (tenantId, tenantName) => {
    try {
      const res = await fetch(`${API}/platform/impersonate/${tenantId}`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        await checkAuth();
        toast.success(`Viewing as ${tenantName}`);
        navigate('/dashboard');
      }
    } catch (e) {
      console.error('Impersonation failed:', e);
      toast.error('Failed to enter tenant');
    }
  };

  const doSuspend = async () => {
    const t = suspendTarget;
    if (!t) return;
    setSuspendBusy(true);
    try {
      const res = await fetch(`${API}/platform/tenants/${t.tenant_id}/suspend`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: suspendReason.trim() || null }),
      });
      if (!res.ok) throw new Error();
      setTenants(prev => prev.map(x => x.tenant_id === t.tenant_id
        ? { ...x, suspended: true, suspension_reason: suspendReason.trim() || null } : x));
      toast.success(`Access suspended for ${t.name}`);
      setSuspendTarget(null);
      setSuspendReason('');
    } catch (e) {
      toast.error('Could not suspend access');
    } finally {
      setSuspendBusy(false);
    }
  };

  const doUnsuspend = async (t) => {
    try {
      const res = await fetch(`${API}/platform/tenants/${t.tenant_id}/unsuspend`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) throw new Error();
      setTenants(prev => prev.map(x => x.tenant_id === t.tenant_id ? { ...x, suspended: false } : x));
      toast.success(`Access restored for ${t.name}`);
    } catch (e) {
      toast.error('Could not restore access');
    }
  };


  const handleCreate = async () => {
    if (!newTenant.name || !newTenant.admin_email) {
      toast.error('Tenant name and admin email are required');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/platform/tenants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newTenant),
      });
      if (res.ok) {
        const data = await res.json();
        setCreatedCreds(data);
        toast.success(`Tenant "${data.name}" created`);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create tenant');
      }
    } catch (e) {
      console.error('Create tenant failed:', e);
      toast.error('Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  const handlePlanChange = async (tenantId, newPlan) => {
    setUpdatingPlanFor(tenantId);
    try {
      const res = await fetch(`${API}/platform/tenants/${tenantId}/plan`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan_type: newPlan }),
      });
      if (res.ok) {
        toast.success('Plan updated');
        setTenants(prev => prev.map(t => t.tenant_id === tenantId ? { ...t, plan_type: newPlan } : t));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update plan');
      }
    } catch (e) {
      console.error('Plan update failed:', e);
      toast.error('Failed to update plan');
    } finally {
      setUpdatingPlanFor(null);
    }
  };

  // Toggle a single feature flag for a tenant. Optimistic update so the switch
  // flips immediately; revert on server failure so the UI stays truthful.
  const toggleFeatureFlag = async (tenantId, key, value) => {
    const existing = tenants.find(t => t.tenant_id === tenantId);
    const current = { ...DEFAULT_FEATURE_FLAGS, ...(existing?.feature_flags || {}) };
    const merged = { ...current, [key]: value };
    setFeaturesSaving(true);
    setTenants(prev => prev.map(t => t.tenant_id === tenantId ? { ...t, feature_flags: merged } : t));
    setFeaturesTenant(prev => (prev && prev.tenant_id === tenantId ? { ...prev, feature_flags: merged } : prev));
    try {
      const res = await fetch(`${API}/tenants/${tenantId}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ feature_flags: { [key]: value } }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update feature');
        // Revert on failure.
        setTenants(prev => prev.map(t => t.tenant_id === tenantId ? { ...t, feature_flags: current } : t));
        setFeaturesTenant(prev => (prev && prev.tenant_id === tenantId ? { ...prev, feature_flags: current } : prev));
      }
    } catch {
      toast.error('Failed to update feature');
      setTenants(prev => prev.map(t => t.tenant_id === tenantId ? { ...t, feature_flags: current } : t));
      setFeaturesTenant(prev => (prev && prev.tenant_id === tenantId ? { ...prev, feature_flags: current } : prev));
    } finally {
      setFeaturesSaving(false);
    }
  };

  // Mystery Shopping notification toggle (per-tenant). Optimistic + revert on fail.
  const toggleNotifyClientOnPublish = async (tenantId, value) => {
    const existing = tenants.find(t => t.tenant_id === tenantId);
    const previous = !!existing?.notify_client_on_publish;
    setFeaturesSaving(true);
    setTenants(prev => prev.map(t => t.tenant_id === tenantId ? { ...t, notify_client_on_publish: value } : t));
    setFeaturesTenant(prev => (prev && prev.tenant_id === tenantId ? { ...prev, notify_client_on_publish: value } : prev));
    try {
      const res = await fetch(`${API}/tenants/${tenantId}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notify_client_on_publish: value }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update notification setting');
        setTenants(prev => prev.map(t => t.tenant_id === tenantId ? { ...t, notify_client_on_publish: previous } : t));
        setFeaturesTenant(prev => (prev && prev.tenant_id === tenantId ? { ...prev, notify_client_on_publish: previous } : prev));
      }
    } catch {
      toast.error('Failed to update notification setting');
      setTenants(prev => prev.map(t => t.tenant_id === tenantId ? { ...t, notify_client_on_publish: previous } : t));
      setFeaturesTenant(prev => (prev && prev.tenant_id === tenantId ? { ...prev, notify_client_on_publish: previous } : prev));
    } finally {
      setFeaturesSaving(false);
    }
  };

  const toggleServiceExcellence = async (tenantId, value) => {
    // Staff Recognition (Service Excellence) certificate opt-in — separate
    // top-level tenant field, per-client. Writes ONLY on user action via the
    // existing authorised endpoint; never initialised on load.
    const existing = tenants.find(t => t.tenant_id === tenantId);
    const previous = !!existing?.service_excellence_certificates_enabled;
    setFeaturesSaving(true);
    setTenants(prev => prev.map(t => t.tenant_id === tenantId ? { ...t, service_excellence_certificates_enabled: value } : t));
    setFeaturesTenant(prev => (prev && prev.tenant_id === tenantId ? { ...prev, service_excellence_certificates_enabled: value } : prev));
    try {
      const res = await fetch(`${API}/platform/tenants/${tenantId}/service-excellence`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: value }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update Staff Recognition setting');
        setTenants(prev => prev.map(t => t.tenant_id === tenantId ? { ...t, service_excellence_certificates_enabled: previous } : t));
        setFeaturesTenant(prev => (prev && prev.tenant_id === tenantId ? { ...prev, service_excellence_certificates_enabled: previous } : prev));
      } else {
        toast.success(`Staff Recognition Certificates ${value ? 'enabled' : 'disabled'}`);
      }
    } catch {
      toast.error('Failed to update Staff Recognition setting');
      setTenants(prev => prev.map(t => t.tenant_id === tenantId ? { ...t, service_excellence_certificates_enabled: previous } : t));
      setFeaturesTenant(prev => (prev && prev.tenant_id === tenantId ? { ...prev, service_excellence_certificates_enabled: previous } : prev));
    } finally {
      setFeaturesSaving(false);
    }
  };


  const saveQuestionLimitOverride = async (tenantId, rawValue) => {
    // Empty / 0 / negative → clear the override (plan default applies).
    const trimmed = (rawValue ?? '').toString().trim();
    let body;
    if (trimmed === '' || trimmed === '0') {
      body = { question_limit_override: null };
    } else {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 1 || n > 1000) {
        toast.error('Question limit override must be between 1 and 1000');
        return;
      }
      body = { question_limit_override: Math.trunc(n) };
    }

    // Skip the network round-trip when nothing actually changed.
    const currentVal = featuresTenant?.question_limit_override ?? null;
    const nextVal = body.question_limit_override;
    if ((currentVal ?? null) === (nextVal ?? null)) return;

    setFeaturesSaving(true);
    try {
      const res = await fetch(`${API}/tenants/${tenantId}/limits`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save override');
        return;
      }
      const data = await res.json();
      const stored = data.question_limit_override ?? null;
      setTenants(prev => prev.map(t => t.tenant_id === tenantId ? { ...t, question_limit_override: stored } : t));
      setFeaturesTenant(prev => (prev && prev.tenant_id === tenantId ? { ...prev, question_limit_override: stored } : prev));
      toast.success(stored ? `Question limit set to ${stored}` : 'Question limit override cleared');
    } catch (e) {
      console.error('Save override failed:', e);
      toast.error('Failed to save override');
    } finally {
      setFeaturesSaving(false);
    }
  };


  // ─── Subscription editor (Platform Manager) ─────────────────────────────
  // Opens the modal seeded from the tenant row's current subscription snapshot.
  const openSubscriptionEditor = (t) => {
    const s = t.subscription || {};
    setSubscriptionTenant(t);
    setSubscriptionDraft({
      status:          s.status || '',
      source:          s.source || '',
      billing_cycle:   s.billing_cycle || '',
      ends_at:         s.ends_at || '',
      read_only_until: s.read_only_until || '',
      archived_at:     s.archived_at || '',
    });
    // Load current location allowance + usage for this tenant.
    setLocAllowance(null);
    setLocAllowanceInput('');
    fetch(`${API}/platform/tenants/${t.tenant_id}/location-allowance`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data) {
          setLocAllowance(data);
          setLocAllowanceInput(String(data.quantity ?? 0));
        }
      })
      .catch(() => {});
  };

  const saveLocationAllowance = async () => {
    if (!subscriptionTenant) return;
    const n = parseInt(locAllowanceInput, 10);
    if (isNaN(n) || n < 0) { toast.error('Enter a whole number of locations (0 or more).'); return; }
    setLocAllowanceSaving(true);
    try {
      const res = await fetch(`${API}/platform/tenants/${subscriptionTenant.tenant_id}/location-allowance`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: n }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || `Failed to update allowance (HTTP ${res.status})`);
        return;
      }
      setLocAllowance(data);
      setLocAllowanceInput(String(data.quantity ?? n));
      toast.success(`Location allowance set to ${data.quantity}`);
    } catch (e) {
      console.error('Save allowance failed:', e);
      toast.error('Failed to update location allowance');
    } finally {
      setLocAllowanceSaving(false);
    }
  };

  const saveSubscription = async () => {
    if (!subscriptionTenant) return;
    const tid = subscriptionTenant.tenant_id;
    // Only send keys the operator actually touched; empty strings become null
    // so the backend can clear a value. The endpoint additively merges.
    const payload = {};
    Object.entries(subscriptionDraft).forEach(([k, v]) => {
      payload[k] = (typeof v === 'string' && v.trim() === '') ? null : v;
    });
    setSubscriptionSaving(true);
    try {
      const res = await fetch(`${API}/platform/tenants/${tid}/subscription`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed to update subscription (HTTP ${res.status})`);
        return;
      }
      const data = await res.json();
      // Refresh the tenant row from the server response so the badge + editor
      // stay in sync with what was actually persisted.
      setTenants(prev => prev.map(t => t.tenant_id === tid ? {
        ...t,
        subscription: data.subscription || {},
        subscription_status: (data.subscription && data.subscription.status) || 'none',
        subscription_plan:   (data.subscription && data.subscription.plan_id) || t.subscription_plan,
      } : t));
      toast.success('Subscription updated');
      setSubscriptionTenant(null);
    } catch (e) {
      console.error('Save subscription failed:', e);
      toast.error('Failed to update subscription');
    } finally {
      setSubscriptionSaving(false);
    }
  };

  const filteredTenants = tenants.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    (t.admin_email || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleRegenerateQrs = async () => {
    const ok = window.confirm(
      'Regenerate every survey QR PNG across all tenants using the current public URL?\n\nThis cannot be undone, and existing printed QR codes will no longer work.'
    );
    if (!ok) return;
    setRegeneratingQrs(true);
    try {
      const res = await fetch(`${API}/admin/regenerate-qrs`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Regenerated ${data.regenerated} QR code(s) → ${data.public_base_url}`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to regenerate QR codes');
      }
    } catch (e) {
      console.error('Regenerate QRs failed:', e);
      toast.error('Failed to regenerate QR codes');
    } finally {
      setRegeneratingQrs(false);
    }
  };

  const handleClearResponses = async (tenantId, tenantName, responseCount) => {
    const ok = window.confirm(
      `Delete ALL ${responseCount || ''} survey responses for "${tenantName}"?\n\n` +
      `Surveys, branches and QR codes will NOT be touched. This cannot be undone.`
    );
    if (!ok) return;
    setClearingResponsesFor(tenantId);
    try {
      const res = await fetch(`${API}/admin/tenants/${tenantId}/responses`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Deleted ${data.deleted_count} response(s) for ${tenantName}`);
        setTenants(prev => prev.map(t => t.tenant_id === tenantId ? { ...t, response_count: 0 } : t));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to clear responses');
      }
    } catch (e) {
      console.error('Clear responses failed:', e);
      toast.error('Failed to clear responses');
    } finally {
      setClearingResponsesFor(null);
    }
  };


  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="platform-dashboard">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">Platform Dashboard</h1>
            <p className="text-sm text-[#64748B] mt-1">Manage all satisFIND tenants</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={handleRegenerateQrs}
              disabled={regeneratingQrs}
              variant="outline"
              className="border-[#1A3E6F]/30 text-[#1A3E6F] hover:bg-[#1A3E6F]/5"
              data-testid="regenerate-qrs-btn"
            >
              <QrCode className="w-4 h-4 mr-2" />
              {regeneratingQrs ? 'Regenerating…' : 'Regenerate QR Codes'}
            </Button>
            <Button
              onClick={() => { setCreateOpen(true); setCreatedCreds(null); setNewTenant({ name: '', admin_email: '', admin_name: '', industry: 'restaurant', plan_type: 'vox_core' }); }}
              className="bg-[#1A3E6F] hover:bg-[#15325A] text-white"
              data-testid="create-tenant-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New Tenant
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-[#1A3E6F]/10">
                  <Globe className="w-5 h-5 text-[#1A3E6F]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0F172A]">{stats?.total_tenants || 0}</p>
                  <p className="text-xs text-[#64748B]">Total Tenants</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-[#5BC4BF]/10">
                  <Building2 className="w-5 h-5 text-[#5BC4BF]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0F172A]">{stats?.total_branches || 0}</p>
                  <p className="text-xs text-[#64748B]">Total Branches</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-[#F1D511]/10">
                  <MessageSquare className="w-5 h-5 text-[#D4A90A]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0F172A]">{stats?.total_responses || 0}</p>
                  <p className="text-xs text-[#64748B]">Total Responses</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-[#D62B27]/10">
                  <CreditCard className="w-5 h-5 text-[#D62B27]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0F172A]">{stats?.active_subscriptions || 0}</p>
                  <p className="text-xs text-[#64748B]">Active Subs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity row */}
        <div className="flex gap-4">
          <Card className="flex-1 border-0 shadow-sm">
            <CardContent className="pt-4 pb-3 px-5 flex items-center gap-3">
              <TrendingUp className="w-4 h-4 text-[#5BC4BF]" />
              <span className="text-sm text-[#64748B]">{stats?.recent_responses_7d || 0} responses this week</span>
            </CardContent>
          </Card>
          <Card className="flex-1 border-0 shadow-sm">
            <CardContent className="pt-4 pb-3 px-5 flex items-center gap-3">
              <Users className="w-4 h-4 text-[#1A3E6F]" />
              <span className="text-sm text-[#64748B]">{stats?.total_users || 0} total users across all tenants</span>
            </CardContent>
          </Card>
        </div>

        {/* Product Features (platform-wide) */}
        <ProductFeaturesPanel />

        {/* Tenant List */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-[#0F172A]">All Tenants</CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search tenants..."
                  className="pl-9 text-sm h-9"
                  data-testid="tenant-search"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tenant-table">
                <thead>
                  <tr className="border-b border-[#E2E8F0] text-left text-xs text-[#64748B] uppercase tracking-wide">
                    <th className="pb-3 pr-4 font-medium">Tenant</th>
                    <th className="pb-3 pr-4 font-medium">Admin</th>
                    <th className="pb-3 pr-4 font-medium text-center">Branches</th>
                    <th className="pb-3 pr-4 font-medium text-center">Responses</th>
                    <th className="pb-3 pr-4 font-medium text-center">Users</th>
                    <th className="pb-3 pr-4 font-medium">Plan</th>
                    <th className="pb-3 pr-4 font-medium">Subscription</th>
                    <th className="pb-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTenants.map((t) => (
                    <tr key={t.tenant_id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC] transition-colors">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-3">
                          {t.branding?.logo ? (
                            <img src={t.branding.logo} alt="" className="w-8 h-8 rounded-lg object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-lg bg-[#1A3E6F]/10 flex items-center justify-center">
                              <Building2 className="w-4 h-4 text-[#1A3E6F]" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-[#0F172A]">{t.name}</p>
                              {t.suspended && (
                                <Badge className="bg-[#FEE2E2] text-[#B91C1C] border-0 text-[10px] gap-1" data-testid={`suspended-badge-${t.tenant_id}`}>
                                  <ShieldAlert className="w-3 h-3" /> Access suspended
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-[#94A3B8]">{t.industry}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <p className="text-[#334155]">{t.admin_name || '-'}</p>
                        <p className="text-xs text-[#94A3B8]">{t.admin_email || '-'}</p>
                      </td>
                      <td className="py-3 pr-4 text-center font-medium text-[#334155]">{t.branch_count}</td>
                      <td className="py-3 pr-4 text-center font-medium text-[#334155]">{t.response_count}</td>
                      <td className="py-3 pr-4 text-center font-medium text-[#334155]">{t.user_count}</td>
                      <td className="py-3 pr-4">
                        {/* If this tenant is on a legacy plan_type that the
                            backend filters out of /platform/plans (e.g.
                            `nps_pro`), include it as an extra SelectItem so
                            the dropdown still displays the current value
                            instead of going blank. We never auto-rewrite it. */}
                        {(() => {
                          // Feb 2026 — every plan_type that was moved to
                          // PLAN_LEGACY_ALIASES gets a friendly legacy label
                          // so existing tenants render clearly in the
                          // dropdown even though the option is hidden from
                          // /platform/plans. We NEVER auto-rewrite the value.
                          const legacyLabelByType = {
                            nps_pro:      'NPS® Pro (legacy)',
                            nps_paid:     'NPS® Paid (legacy)',
                            free_nps:     'NPS® Free (legacy)',
                            nps_free:     'NPS® Free (legacy)',
                            vox_core:     'VOX Core (legacy)',
                            vox_plus:     'VOX Plus (legacy)',
                            vox_custom:   'VOX Custom (legacy)',
                            trust_circle: 'Trust Circle (legacy)',
                          };
                          // Fallback default aligns with the new canonical
                          // dropdown; used only when a tenant has no plan_type
                          // stored yet (extremely rare).
                          const currentValue = t.plan_type || 'vox';
                          const inOptions = planOptions.some(p => p.plan_type === currentValue);
                          const showLegacyRow = !inOptions && !!t.plan_type;
                          return (
                            <Select
                              value={currentValue}
                              onValueChange={(v) => handlePlanChange(t.tenant_id, v)}
                              disabled={updatingPlanFor === t.tenant_id}
                            >
                              <SelectTrigger className="h-8 w-[140px] text-xs" data-testid={`plan-select-${t.tenant_id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {showLegacyRow && (
                                  <SelectItem
                                    key={currentValue}
                                    value={currentValue}
                                    className="text-xs text-[#94A3B8]"
                                    data-testid={`plan-option-${currentValue}`}
                                  >
                                    {legacyLabelByType[currentValue] || currentValue}
                                  </SelectItem>
                                )}
                                {planOptions.map(p => (
                                  <SelectItem key={p.plan_type} value={p.plan_type} className="text-xs" data-testid={`plan-option-${p.plan_type}`}>
                                    {p.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          );
                        })()}
                      </td>
                      <td className="py-3 pr-4">
                        {/* Feb 2026 — the Subscription column reflects
                            `subscription.status` ONLY. Product / plan lives
                            in the neighbouring PLAN column. Reads
                            `subscription_status` from list_tenants (which
                            is `subscription.status` or `"none"`). */}
                        {(() => {
                          const status = t.subscription_status;
                          const isActive = status === 'active';
                          const isMissing = !status || status === 'none';
                          const label = isMissing
                            ? 'No Plan'
                            : isActive
                              ? 'Active'
                              : status; // e.g. pending_payment, past_due, expired…
                          return (
                            <Badge
                              variant={isActive ? 'default' : 'secondary'}
                              className={isActive
                                ? 'bg-[#5BC4BF]/15 text-[#0D9488] border-0'
                                : 'bg-[#F1F5F9] text-[#64748B] border-0'
                              }
                              data-testid={`subscription-badge-${t.tenant_id}`}
                            >
                              {label}
                            </Badge>
                          );
                        })()}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setFeaturesTenant(t)}
                            className="text-xs h-8 gap-1.5 border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
                            data-testid={`features-tenant-${t.tenant_id}`}
                          >
                            <SlidersHorizontal className="w-3.5 h-3.5" /> Features
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openSubscriptionEditor(t)}
                            className="text-xs h-8 gap-1.5 border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
                            data-testid={`subscription-tenant-${t.tenant_id}`}
                          >
                            <CreditCard className="w-3.5 h-3.5" /> Subscription
                          </Button>
                          {isPlatformAdmin && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleClearResponses(t.tenant_id, t.name, t.response_count)}
                              disabled={clearingResponsesFor === t.tenant_id}
                              className="text-xs h-8 gap-1.5 border-[#D62B27]/30 text-[#D62B27] hover:bg-[#D62B27]/5"
                              data-testid={`clear-responses-${t.tenant_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              {clearingResponsesFor === t.tenant_id ? 'Clearing…' : 'Clear All Responses'}
                            </Button>
                          )}
                          {isPlatformAdmin && (
                            t.suspended ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => doUnsuspend(t)}
                                className="text-xs h-8 gap-1.5 border-[#0D9488]/30 text-[#0D9488] hover:bg-[#0D9488]/5"
                                data-testid={`restore-access-${t.tenant_id}`}
                              >
                                <ShieldCheck className="w-3.5 h-3.5" /> Restore access
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setSuspendTarget(t); setSuspendReason(''); }}
                                className="text-xs h-8 gap-1.5 border-[#D62B27]/30 text-[#D62B27] hover:bg-[#D62B27]/5"
                                data-testid={`suspend-access-${t.tenant_id}`}
                              >
                                <ShieldAlert className="w-3.5 h-3.5" /> Suspend access
                              </Button>
                            )
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleImpersonate(t.tenant_id, t.name)}
                            className="text-xs h-8 gap-1.5 border-[#1A3E6F]/20 text-[#1A3E6F] hover:bg-[#1A3E6F]/5"
                            data-testid={`enter-tenant-${t.tenant_id}`}
                          >
                            <LogIn className="w-3.5 h-3.5" /> Enter
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredTenants.length === 0 && (
                    <tr>
                      <td colSpan="8" className="py-8 text-center text-[#94A3B8]">
                        {search ? 'No tenants match your search' : 'No tenants yet'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Create Tenant Modal */}
        {/* Suspend-access confirmation */}
        {suspendTarget && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="suspend-confirm-modal">
            <div className="bg-white rounded-2xl w-full max-w-md p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FEE2E2] flex items-center justify-center shrink-0">
                  <ShieldAlert className="w-5 h-5 text-[#DC2626]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A]">Suspend access</h3>
                  <p className="text-sm text-[#64748B] mt-1">
                    You're about to suspend access for <b className="text-[#0F172A]">{suspendTarget.name}</b>. All of this client's
                    users will be locked out of the platform immediately. Surveys, responses, reports, files and accounts are
                    preserved, respondents can still submit surveys, and you can restore access at any time.
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-xs font-medium text-[#475569]">Internal reason (optional — not shown to the client)</label>
                <textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  rows={3}
                  className="mt-1 w-full border border-[#E2E8F0] rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3E6F]/20"
                  placeholder="e.g. Overdue payment — invoice #1234"
                  data-testid="suspend-reason-input"
                />
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setSuspendTarget(null); setSuspendReason(''); }} data-testid="suspend-cancel">Cancel</Button>
                <Button onClick={doSuspend} disabled={suspendBusy} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="suspend-confirm">
                  {suspendBusy ? 'Suspending…' : 'Suspend access'}
                </Button>
              </div>
            </div>
          </div>
        )}


        {createOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-tenant-modal">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
              <div className="flex items-center justify-between p-5 border-b border-[#E2E8F0]">
                <h2 className="text-lg font-semibold text-[#0F172A]">
                  {createdCreds ? 'Tenant Created' : 'Create New Tenant'}
                </h2>
                <button onClick={() => setCreateOpen(false)} className="p-1 hover:bg-[#F1F5F9] rounded-lg">
                  <X className="w-5 h-5 text-[#64748B]" />
                </button>
              </div>

              {createdCreds ? (
                <div className="p-5 space-y-4">
                  <div className="p-4 bg-[#F0FDF4] rounded-lg border border-[#BBF7D0]">
                    <p className="text-sm font-medium text-[#166534] mb-2">Tenant created successfully</p>
                    <div className="space-y-2 text-sm">
                      <div><span className="text-[#64748B]">Tenant:</span> <span className="font-medium text-[#0F172A]">{createdCreds.name}</span></div>
                      <div><span className="text-[#64748B]">Admin Email:</span> <span className="font-mono text-[#0F172A]">{createdCreds.admin_email}</span></div>
                      <div><span className="text-[#64748B]">Password:</span> <span className="font-mono text-[#0F172A] bg-[#FEF9C3] px-1.5 py-0.5 rounded">{createdCreds.admin_password}</span></div>
                    </div>
                  </div>
                  <p className="text-xs text-[#94A3B8]">Save these credentials — the password won&apos;t be shown again.</p>
                  <Button onClick={() => setCreateOpen(false)} className="w-full bg-[#1A3E6F] hover:bg-[#15325A] text-white">Done</Button>
                </div>
              ) : (
                <div className="p-5 space-y-4">
                  <div>
                    <label className="text-xs font-medium text-[#64748B] mb-1.5 block">Tenant Name *</label>
                    <Input
                      value={newTenant.name}
                      onChange={(e) => setNewTenant(p => ({ ...p, name: e.target.value }))}
                      placeholder="e.g. Jollibee Philippines"
                      data-testid="new-tenant-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#64748B] mb-1.5 block">Admin Email *</label>
                    <Input
                      value={newTenant.admin_email}
                      onChange={(e) => setNewTenant(p => ({ ...p, admin_email: e.target.value }))}
                      placeholder="admin@company.com"
                      data-testid="new-tenant-email"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#64748B] mb-1.5 block">Admin Name</label>
                    <Input
                      value={newTenant.admin_name}
                      onChange={(e) => setNewTenant(p => ({ ...p, admin_name: e.target.value }))}
                      placeholder="John Doe"
                      data-testid="new-tenant-admin-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#64748B] mb-1.5 block">Industry</label>
                    <Input
                      value={newTenant.industry}
                      onChange={(e) => setNewTenant(p => ({ ...p, industry: e.target.value }))}
                      placeholder="restaurant"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#64748B] mb-1.5 block">Product / Plan</label>
                    <select
                      value={newTenant.plan_type}
                      onChange={(e) => setNewTenant(p => ({ ...p, plan_type: e.target.value }))}
                      className="w-full h-10 rounded-md border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F172A]"
                      data-testid="create-tenant-plan"
                    >
                      {(planOptions || []).map(pl => (
                        <option key={pl.plan_type} value={pl.plan_type}>{pl.label}</option>
                      ))}
                    </select>
                    <p className="text-[11px] text-[#94A3B8] mt-1">Sets the client's product and access from creation. Pick Mystery Shopping for offline MS clients.</p>
                  </div>
                  <Button
                    onClick={handleCreate}
                    disabled={creating}
                    className="w-full bg-[#1A3E6F] hover:bg-[#15325A] text-white"
                    data-testid="submit-create-tenant"
                  >
                    {creating ? 'Creating...' : 'Create Tenant'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Features Modal (platform_admin feature-flag control per tenant) */}
        {featuresTenant && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setFeaturesTenant(null)}
            data-testid="features-modal-backdrop"
          >
            <div
              className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="features-modal"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-[#0F172A]">Features</h3>
                  <p className="text-sm text-[#64748B] mt-0.5">{featuresTenant.name}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setFeaturesTenant(null)} className="h-8 w-8">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-3">
                {FEATURE_FLAG_DEFS.map(f => {
                  const flags = { ...DEFAULT_FEATURE_FLAGS, ...(featuresTenant.feature_flags || {}) };
                  return (
                    <div
                      key={f.key}
                      className="flex items-center justify-between border border-[#E2E8F0] rounded-md px-3 py-2.5"
                    >
                      <span className="text-sm text-[#0F172A]">{f.label}</span>
                      <Switch
                        checked={!!flags[f.key]}
                        onCheckedChange={(v) => toggleFeatureFlag(featuresTenant.tenant_id, f.key, v)}
                        disabled={featuresSaving}
                        data-testid={`feature-toggle-${f.key}`}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Mystery Shopping: notify client on publish */}
              <div className="border-t border-[#E2E8F0] mt-5 pt-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 pr-3">
                    <p className="text-sm font-medium text-[#0F172A]">Notify client when a FIND is published</p>
                    <p className="text-[11px] text-[#94A3B8]">
                      Mystery Shopping only — emails the tenant admin a link to the published report on publish.
                    </p>
                  </div>
                  <Switch
                    checked={!!featuresTenant.notify_client_on_publish}
                    onCheckedChange={(v) => toggleNotifyClientOnPublish(featuresTenant.tenant_id, v)}
                    disabled={featuresSaving}
                    data-testid="notify-client-on-publish-toggle"
                  />
                </div>
              </div>

              {/* Staff Recognition Certificates — per-client opt-in.
                  Mystery Shopping only (entitlement OR verified legacy MS
                  fallback). Independent of the dashboard beta flags above and
                  of the platform-wide Management Insights / Ask satisFIND
                  switches. Backed by the separate top-level
                  `service_excellence_certificates_enabled` field via the
                  existing authorised endpoint. Value is only ever written on
                  toggle — never initialised on load. */}
              {featuresTenant.owns_mystery_shopping && (
                <div className="border-t border-[#E2E8F0] mt-5 pt-4" data-testid="staff-recognition-section">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 pr-3">
                      <p className="text-sm font-medium text-[#0F172A]">Staff Recognition Certificates</p>
                      <p className="text-[11px] text-[#94A3B8]">
                        Mystery Shopping only — lets this client issue Service Excellence
                        certificates from eligible FINDs. Off by default; affects only this client.
                      </p>
                    </div>
                    <Switch
                      checked={!!featuresTenant.service_excellence_certificates_enabled}
                      onCheckedChange={(v) => toggleServiceExcellence(featuresTenant.tenant_id, v)}
                      disabled={featuresSaving}
                      data-testid="staff-recognition-toggle"
                    />
                  </div>
                </div>
              )}

              {/* Question-limit override (per-tenant). Empty / 0 = use plan default. */}
              <div className="border-t border-[#E2E8F0] mt-5 pt-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0F172A]">Question limit override</p>
                    <p className="text-[11px] text-[#94A3B8]">
                      Replaces the plan&apos;s question cap for this tenant. Leave blank or 0 to use the plan default.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Input
                      type="number"
                      min={0}
                      max={1000}
                      defaultValue={featuresTenant.question_limit_override ?? ''}
                      placeholder="Use plan default"
                      className="w-32 h-9 text-sm"
                      data-testid="question-limit-override-input"
                      onBlur={(e) => saveQuestionLimitOverride(featuresTenant.tenant_id, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      disabled={featuresSaving}
                    />
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-[#94A3B8] mt-4">
                Changes take effect on this tenant&apos;s next page load.
              </p>
            </div>
          </div>
        )}

        {/* Feb 2026 — Subscription editor modal. Platform-Manager-only.
            Deliberately does NOT touch plan_type, question limits, feature
            flags or access enforcement — it only reads/writes the
            `tenants.subscription` sub-doc so operators can classify
            existing bank-transfer/paid clients ahead of a future
            payment-access rollout. */}
        {subscriptionTenant && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => !subscriptionSaving && setSubscriptionTenant(null)}
            data-testid="subscription-modal-backdrop"
          >
            <div
              className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="subscription-modal"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-[#0F172A]">Subscription</h3>
                  <p className="text-sm text-[#64748B] mt-0.5">{subscriptionTenant.name}</p>
                </div>
                <Button
                  variant="ghost" size="icon"
                  onClick={() => setSubscriptionTenant(null)}
                  disabled={subscriptionSaving}
                  className="h-8 w-8"
                  data-testid="subscription-modal-close"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* Status */}
                <div>
                  <label className="text-xs font-medium text-[#64748B] mb-1 block">Status</label>
                  <Select
                    value={subscriptionDraft.status || '__none__'}
                    onValueChange={(v) => setSubscriptionDraft(d => ({ ...d, status: v === '__none__' ? '' : v }))}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid="subscription-status-select">
                      <SelectValue placeholder="Not set" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" className="text-xs text-[#94A3B8]">— Not set —</SelectItem>
                      {SUBSCRIPTION_STATUS_OPTIONS.map(v => (
                        <SelectItem key={v} value={v} className="text-xs" data-testid={`subscription-status-option-${v}`}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Source */}
                <div>
                  <label className="text-xs font-medium text-[#64748B] mb-1 block">Source</label>
                  <Select
                    value={subscriptionDraft.source || '__none__'}
                    onValueChange={(v) => setSubscriptionDraft(d => ({ ...d, source: v === '__none__' ? '' : v }))}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid="subscription-source-select">
                      <SelectValue placeholder="Not set" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" className="text-xs text-[#94A3B8]">— Not set —</SelectItem>
                      {SUBSCRIPTION_SOURCE_OPTIONS.map(v => (
                        <SelectItem key={v} value={v} className="text-xs" data-testid={`subscription-source-option-${v}`}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Billing cycle */}
                <div>
                  <label className="text-xs font-medium text-[#64748B] mb-1 block">Billing cycle</label>
                  <Select
                    value={subscriptionDraft.billing_cycle || '__none__'}
                    onValueChange={(v) => setSubscriptionDraft(d => ({ ...d, billing_cycle: v === '__none__' ? '' : v }))}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid="subscription-billing-cycle-select">
                      <SelectValue placeholder="Not set" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" className="text-xs text-[#94A3B8]">— Not set —</SelectItem>
                      {SUBSCRIPTION_BILLING_CYCLE_OPTIONS.map(v => (
                        <SelectItem key={v} value={v} className="text-xs" data-testid={`subscription-billing-cycle-option-${v}`}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Grace-period fields — stored/viewable/editable only. Not
                    enforced yet. Free-form strings so operators can paste
                    whatever format they have (ISO 8601 recommended). */}
                <div className="border-t border-[#E2E8F0] pt-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-3">
                    Grace period (stored only — not enforced)
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-[#64748B] mb-1 block">Ends at</label>
                      <Input
                        type="text"
                        placeholder="YYYY-MM-DD"
                        value={subscriptionDraft.ends_at || ''}
                        onChange={(e) => setSubscriptionDraft(d => ({ ...d, ends_at: e.target.value }))}
                        className="h-9 text-sm"
                        data-testid="subscription-ends-at-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#64748B] mb-1 block">Read-only until</label>
                      <Input
                        type="text"
                        placeholder="YYYY-MM-DD"
                        value={subscriptionDraft.read_only_until || ''}
                        onChange={(e) => setSubscriptionDraft(d => ({ ...d, read_only_until: e.target.value }))}
                        className="h-9 text-sm"
                        data-testid="subscription-read-only-until-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#64748B] mb-1 block">Archived at</label>
                      <Input
                        type="text"
                        placeholder="YYYY-MM-DD"
                        value={subscriptionDraft.archived_at || ''}
                        onChange={(e) => setSubscriptionDraft(d => ({ ...d, archived_at: e.target.value }))}
                        className="h-9 text-sm"
                        data-testid="subscription-archived-at-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Oct 2026 — Location allowance for offline / manually-managed
                    accounts. Lets an operator grant purchased locations WITHOUT
                    Stripe (branch creation is enforced up to this number).
                    Stripe-managed accounts are read-only here — their location
                    count stays controlled via Stripe Add/Reduce locations. */}
                <div className="border-t border-[#E2E8F0] pt-4" data-testid="location-allowance-section">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#94A3B8] mb-3">
                    Location allowance
                  </p>
                  {locAllowance === null ? (
                    <p className="text-xs text-[#94A3B8]" data-testid="location-allowance-loading">Loading…</p>
                  ) : locAllowance.is_stripe_managed ? (
                    <p className="text-xs text-[#64748B]" data-testid="location-allowance-stripe-note">
                      This account is billed through Stripe. Location count is managed via the client's
                      Subscription page (Add / Reduce locations) and can't be set manually here.
                    </p>
                  ) : !locAllowance.is_offline ? (
                    <p className="text-xs text-[#64748B]" data-testid="location-allowance-not-offline-note">
                      Manual location allowance applies only to offline / manually-managed subscriptions
                      (set Source to "manual_admin" or Billing cycle to "custom", save, then reopen).
                    </p>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 text-xs text-[#64748B]" data-testid="location-allowance-usage">
                        <span>Purchased: <span className="font-semibold text-[#0F172A]" data-testid="location-allowance-purchased">{locAllowance.quantity}</span></span>
                        <span>Used: <span className="font-semibold text-[#0F172A]" data-testid="location-allowance-used">{locAllowance.used}</span></span>
                        <span>Available: <span className="font-semibold text-[#0F172A]" data-testid="location-allowance-available">{locAllowance.available}</span></span>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-[#64748B] mb-1 block">Purchased locations</label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={0}
                            value={locAllowanceInput}
                            onChange={(e) => setLocAllowanceInput(e.target.value)}
                            className="h-9 text-sm w-32"
                            data-testid="location-allowance-input"
                          />
                          <Button
                            onClick={saveLocationAllowance}
                            disabled={locAllowanceSaving}
                            className="text-xs h-9 bg-[#1A3E6F] hover:bg-[#15325A] text-white"
                            data-testid="location-allowance-save-button"
                          >
                            {locAllowanceSaving ? 'Saving…' : 'Save allowance'}
                          </Button>
                        </div>
                        <p className="text-[11px] text-[#94A3B8] mt-1">
                          Number of locations this offline account may create. Lowering it never removes
                          existing branches — it only blocks new ones beyond the limit.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setSubscriptionTenant(null)}
                  disabled={subscriptionSaving}
                  className="text-xs h-9"
                  data-testid="subscription-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveSubscription}
                  disabled={subscriptionSaving}
                  className="text-xs h-9 bg-[#1A3E6F] hover:bg-[#15325A] text-white"
                  data-testid="subscription-save-button"
                >
                  {subscriptionSaving ? 'Saving…' : 'Save subscription'}
                </Button>
              </div>

              <p className="text-[11px] text-[#94A3B8] mt-4">
                Editing subscription does not change plan, question limits, features or access — those enforcement rules stay off until the payment-access rollout.
              </p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
